import { useEffect, useMemo, useState } from "react";
import {
  MRT_ColumnFiltersState,
  type MRT_ColumnDef,
} from "material-react-table";
import DataTable from "@/components/data-table/DataTable";
import FuseLoading from "@fuse/core/FuseLoading";
import {
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tooltip,
} from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {
  useDeleteUserMutation,
  useGetUsersQuery,
  User,
  useUpdateUserStatusMutation,
  useUpdateUserVerificationMutation,
} from "@/store/services/users";
import ConfirmationDialog from "./Dialog/ConfirmationDialog";
import { toast } from "react-toastify";
import ApplicationsStatus from "@/pages/apps/applications/ApplicationsStatus";
import clsx from "clsx";
import NavLinkAdapter from "@fuse/core/NavLinkAdapter";

function UsersTable({
  limit = 10,
  isDashboard = false,
}: {
  limit?: number;
  isDashboard?: boolean;
}) {
  const getNormalizedSubscriptionStatus = (value: unknown) => {
    const normalized = String(value ?? "").trim().toLowerCase();
    if (normalized === "subscribed") return "subscribed";
    if (normalized === "unsubscribe" || normalized === "unsubscribed") {
      return "unsubscribe";
    }
    return "unsubscribe";
  };

  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState<
    "all" | "subscribed" | "unsubscribe"
  >("all");
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string; email: string } | null>(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: limit,
  });

  const { data: usersData = [], isLoading, isFetching } = useGetUsersQuery({
    key_search: query,
    page: pagination.pageIndex + 1,
    pagination: true,
    per_page: pagination.pageSize,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const showTableLoading = isFetching && !isLoading;

  const [updateUserStatus, { isLoading: isUserStatusLoading }] =
    useUpdateUserStatusMutation();
  const [updateUserVerification, { isLoading: isUserVerificationLoading }] =
    useUpdateUserVerificationMutation();
  const [users, setUsers] = useState<User[]>([]);
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [verificationUpdatingUserId, setVerificationUpdatingUserId] = useState<
    string | null
  >(null);
  const isVerificationPending =
    isUserVerificationLoading && verificationUpdatingUserId !== null;

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setQuery(search);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  useEffect(() => {
    if (usersData?.data?.data) {
      setUsers(usersData?.data?.data);
    }
  }, [usersData]);

  const handleDelete = (id: string, name: string, email: string) => {
    setSelectedUser({ id, name, email });
  };

  const handleDeleteConfirmation = async () => {
    if (!selectedUser) return;

    const response: any = await deleteUser(selectedUser.id);

    if (!response.error) {
      toast.success("User deleted successfully");
    } else {
      toast.error(
        response?.error?.data?.errors?.[0]?.title || "Something went wrong"
      );
    }

    setSelectedUser(null);
  };

  const handleApprovalToggle = async (row: User, checked: boolean) => {
    setVerificationUpdatingUserId(row.id);
    try {
      const response: any = await updateUserVerification({
        user_id: row.id,
        is_verified: checked ? 1 : 0,
      });

      if (response.error) {
        toast.error(
          response?.error?.data?.errors?.[0]?.title || "Failed to update approval"
        );
      } else {
        toast.success(
          checked ? "User approved successfully" : "User unapproved successfully"
        );
      }
    } finally {
      setVerificationUpdatingUserId(null);
    }
  };

  const columns = useMemo<MRT_ColumnDef<User>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        enableColumnDragging: false,
        enableColumnActions: false,
        Cell: ({ row }) => <Typography>{row.original.name}</Typography>,
      },
      ...(isDashboard
        ? []
        : [
            {
              accessorKey: "email",
              header: "Email",
              enableColumnDragging: false,
              enableColumnActions: false,
              Cell: ({ row }) => <Typography>{row.original.email}</Typography>,
            },
          ]),
      {
        accessorKey: "status",
        header: "Status",
        enableColumnDragging: false,
        enableColumnActions: false,
        Cell: ({ row }) => {
          const [status, setStatus] = useState("");
          const handleStatusChange = async (event) => {
            const newStatus = event.target.value;
            setStatus(newStatus);

            const response: any = await updateUserStatus({
              user_id: row.original.id,
              _method: "PUT",
              status: newStatus,
            });

            if (response.error) {
              toast.error(
                response?.error?.data?.errors?.[0]?.title || "Something went wrong"
              );
            } else {
              toast.success("User status updated successfully");
            }
          };

          return isDashboard ? (
            <ApplicationsStatus
              name={row?.original?.status === 1 ? "Active" : "Inactive"}
            />
          ) : (
            <Select
              value={status || row?.original?.status}
              onChange={handleStatusChange}
              size="small"
              sx={{ minWidth: 120 }}
              disabled={isUserStatusLoading}
            >
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="0">In-Active</MenuItem>
            </Select>
          );
        },
      },
      {
        accessorKey: "is_verified",
        header: "Verified",
        enableColumnDragging: false,
        enableColumnActions: false,
        Cell: ({ row }) => (
          <ApplicationsStatus
            name={row?.original?.is_verified ? "Verified" : "Not Verified"}
          />
        ),
      },
      ...(isDashboard
        ? []
        : [
            {
              id: "subscription_status",
              header: "Subscription Status",
              enableColumnDragging: false,
              enableColumnActions: false,
              Cell: ({ row }) => {
                const normalizedStatus = getNormalizedSubscriptionStatus(
                  (row.original as any).subscription_status
                );
                const isSubscribed = normalizedStatus === "subscribed";

                return (
                  <Chip
                    size="small"
                    label={isSubscribed ? "Subscribed" : "Unsubscribe"}
                    sx={{
                      backgroundColor: isSubscribed ? "#0a74da" : "#facc15",
                      color: isSubscribed ? "#ffffff" : "#1f2937",
                      fontWeight: 600,
                    }}
                  />
                );
              },
            },
          ]),
      ...(isDashboard
        ? []
        : [
            {
              accessorKey: "user.actions",
              header: "Actions",
              enableColumnFilter: false,
              enableColumnDragging: false,
              enableSorting: false,
              enableColumnActions: false,
              size: 110,
              Cell: ({ row }) => {
                const isApproved = Number(row.original.is_verified) === 1;
                const isUpdatingCurrentUser =
                  verificationUpdatingUserId === row.original.id;
                const isOtherRowUpdating =
                  isVerificationPending && !isUpdatingCurrentUser;

                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    sx={{
                      opacity: isOtherRowUpdating ? 0.35 : 1,
                      transition: "opacity 0.2s ease-in-out",
                    }}
                  >
                    <Tooltip
                      title={isApproved ? "Click to unapprove user" : "Click to approve user"}
                    >
                      <span className="relative inline-flex items-center justify-center">
                        <Switch
                          size="small"
                          color="success"
                          checked={isApproved}
                          disabled={isVerificationPending}
                          onChange={(_, checked) =>
                            handleApprovalToggle(row.original, checked)
                          }
                          inputProps={{ "aria-label": "approval toggle" }}
                        />
                        {isUpdatingCurrentUser && (
                          <CircularProgress
                            size={18}
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "50%",
                              marginTop: "-8px",
                              marginLeft: "-7px",
                              pointerEvents: "none",
                            }}
                          />
                        )}
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete User">
                      <span>
                      <IconButton
                        aria-label="delete-user"
                        onClick={() => handleDelete(row.original.id, row.original.name, row.original.email)}
                        size="small"
                        disabled={isVerificationPending}
                      >
                        <FuseSvgIcon>heroicons-outline:trash</FuseSvgIcon>
                      </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                );
              },
            },
          ]),
    ],
    [
      isDashboard,
      isUserStatusLoading,
      isUserVerificationLoading,
      verificationUpdatingUserId,
      isVerificationPending,
      getNormalizedSubscriptionStatus,
    ]
  );

  const filteredUsers = useMemo(() => {
    if (isDashboard || subscriptionFilter === "all") {
      return users || [];
    }

    return (users || []).filter(
      (user) =>
        getNormalizedSubscriptionStatus((user as any).subscription_status) ===
        subscriptionFilter
    );
  }, [users, subscriptionFilter, isDashboard, getNormalizedSubscriptionStatus]);

  if (isLoading) {
    return <FuseLoading />;
  }

  const onColumnFiltersChange = (updater) => {
    const newFilters =
      typeof updater === "function" ? updater(columnFilters) : updater;

    if (newFilters.length === 0) {
      setUsers(usersData?.data?.data);
    }

    setColumnFilters(newFilters);
  };

  return (
    <div className="relative">
      <Paper
        className="flex flex-col flex-auto shadow-1 rounded-lg overflow-hidden w-full h-full shadow-sm"
        elevation={0}
      >
        <ConfirmationDialog
          open={!!selectedUser}
          onClose={() => setSelectedUser(null)}
          onConfirm={handleDeleteConfirmation}
          title="Delete User"
          message="Are you sure you want to delete the user"
          itemName={selectedUser?.email}
          isLoading={isDeleting}
        />

        <div className={clsx(isDashboard ? "h-full" : "h-[calc(100vh-147px)]")}>
          <DataTable
            data={filteredUsers}
            columns={columns}
            emptyMessage="No users found."
            query={search}
            setQuery={setSearch}
            enableDensityToggle={false}
            enableSorting={false}
            enableHiding={false}
            enableToolbarInternalActions={false}
            state={{
              columnFilters,
              pagination,
              columnPinning: { right: ["user.actions"] },
              isLoading: showTableLoading,
              showLoadingOverlay: showTableLoading,
              showSkeletons: showTableLoading,
            }}
            onColumnFiltersChange={onColumnFiltersChange}
            rowCount={usersData?.data?.total}
            manualPagination
            onPaginationChange={setPagination}
            enableRowSelection={false}
            searchPlaceholder="Search by name, email"
            enableRowActions={false}
            enableColumnFilters={false}
            enablePagination={!isDashboard}
            enableGlobalFilter={!isDashboard}
            renderTopToolbarCustomActions={({ table }) => {
              const { rowSelection } = table.getState();
              const hasSelection = Object.keys(rowSelection)?.length > 0;

              return (
                <div className="flex items-center gap-2">
                  {!isDashboard && (
                    <FormControl
                      size="small"
                      sx={{ minWidth: 150 }}
                    >
                      <InputLabel
                        id="subscription-status-filter-label"
                        shrink
                      >
                        Subscription Status
                      </InputLabel>
                      <Select
                        labelId="subscription-status-filter-label"
                        label="Subscription Status"
                        value={subscriptionFilter}
                        onChange={(event) =>
                          setSubscriptionFilter(
                            event.target.value as
                              | "all"
                              | "subscribed"
                              | "unsubscribe"
                          )
                        }
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="subscribed">Subscribed</MenuItem>
                        <MenuItem value="unsubscribe">Unsubscribe</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {hasSelection && (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        const selectedRows = table.getSelectedRowModel().rows;
                        deleteUser(selectedRows.map((row) => row.original.id));
                        table.resetRowSelection();
                      }}
                      className="flex shrink min-w-9 ltr:mr-2 rtl:ml-2"
                      color="secondary"
                    >
                      <FuseSvgIcon size={16}>heroicons-outline:trash</FuseSvgIcon>
                      <span className="hidden sm:flex mx-2">
                        Delete selected items
                      </span>
                    </Button>
                  )}
                </div>
              );
            }}
          />
        </div>
      </Paper>
      {isDashboard && usersData?.data?.total > 5 ? (
        <div className="flex justify-center absolute bottom-2.5 w-full z-20">
          <Button
            variant="contained"
            color="secondary"
            component={NavLinkAdapter}
            to="/apps/users"
            size={"medium"}
          >
            <span className="mx-1 sm:mx-2">View More</span>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default UsersTable;
